import React from 'react';
import { PermissionsTypes, ResourcePermissions } from 'model/role-permissions';

/*
 * Hides every trace of profile creation (AI profile generation, write-up
 * drafts, CV import, future joiners, AI credits, the LLM feedback log, AI
 * provenance notes and the "Ask about this page" assistant). Presentation
 * only: the backend and its permissions are unchanged.
 *
 * Two switches, one answer (`isProfileCreationVisible`):
 *  - PROFILE_CREATION_IN_BUILD: for a build that never shows it (e.g. another
 *    studio). Constant, so Vite drops the guarded code from that bundle.
 *  - Demo mode: a per-browser toggle in the side nav (or `?demo=1` / `?demo=0`),
 *    for demos and for testing what that build looks like. Toggling reloads
 *    the page, so nothing loaded while it was visible lingers.
 */
export const PROFILE_CREATION_IN_BUILD = true;

// Permissions treated as Deny while hidden, so everything they gate (sidebar
// items, routes, settings tabs, buttons, data loads) hides itself.
export const PROFILE_CREATION_RESOURCES = [
  'profile_creator_admin',
  'profile_creator_user',
  'profile_creator_all_people',
  'cv_upload',
  'cv_upload_others',
  'future_joiner',
  'llm_feedback',
  'ai_credits_admin',
  'local_lm_studio_user',
  'project_writeup',
];

const STORAGE_KEY = 'hq_demo_mode';

const readStored = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const writeStored = (on: boolean) => {
  try {
    if (on) window.localStorage.setItem(STORAGE_KEY, '1');
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private window / blocked storage: demo mode just won't persist.
  }
};

// `?demo=1` / `?demo=0` sets it (and is then removed from the URL), so a demo
// can start from a link.
const initialDemoMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const url = new URL(window.location.href);
    const param = url.searchParams.get('demo');
    if (param === '1' || param === '0') {
      writeStored(param === '1');
      url.searchParams.delete('demo');
      window.history.replaceState(window.history.state, '', url.toString());
    }
  } catch {
    // Ignore malformed URLs.
  }
  return readStored();
};

// Read once at load: toggling reloads, so this never changes mid-session.
const DEMO_MODE = initialDemoMode();

export const isDemoMode = (): boolean => DEMO_MODE;

export const isProfileCreationVisible = (): boolean => PROFILE_CREATION_IN_BUILD && !DEMO_MODE;

export const setDemoMode = (on: boolean) => {
  writeStored(on);
  window.location.reload();
};

/* Whether this person would see any profile creation without demo mode. */
export const hasProfileCreationGrant = (permissions?: ResourcePermissions): boolean => (
  PROFILE_CREATION_IN_BUILD && PROFILE_CREATION_RESOURCES.some((name) => (
    (permissions?.[name]?.resource || []).some((level) => level !== PermissionsTypes.Deny)))
);

/* The user's permissions as the UI should see them: profile creation denied while hidden. */
export const withProfileCreationPermissions = (permissions: ResourcePermissions): ResourcePermissions => {
  if (isProfileCreationVisible()) return permissions;
  const masked: ResourcePermissions = { ...permissions };
  PROFILE_CREATION_RESOURCES.forEach((name) => {
    if (masked[name]) masked[name] = { ...masked[name], resource: [PermissionsTypes.Deny], properties: {} };
  });
  return masked;
};

export const isProfileCreationResource = (name: string): boolean => PROFILE_CREATION_RESOURCES.includes(name);

/*
 * Wraps a component so it renders nothing (and so fetches nothing) while
 * profile creation is hidden.
 */
export function profileCreationOnly<P extends object>(Component: React.ComponentType<P>) {
  const Wrapped = (props: P) => (isProfileCreationVisible() ? React.createElement(Component, props) : null);
  Wrapped.displayName = `ProfileCreationOnly(${Component.displayName || Component.name || 'Component'})`;
  return Wrapped;
}
